import api from '@Api/Api'
import {
	LoginRequestPayload,
	LoginResponsePayload,
	SignupRequestPayload,
	SignupResponsePayload,
	EducationalInfoResponsePayload,
	CompleteProfileRequestPayload,
	VerifyPhoneRequestPayload,
	VerifyPhoneResponsePayload,
	WaselChatIdentity,
} from '@Features/Authentication/AuthenticationApi/Authentication.api.types'

export default class AuthenticationApi {
	private static endpoint: string = '/auth'

	public static login(requestPayload: LoginRequestPayload) {
		return api.post<LoginResponsePayload>(`${this.endpoint}/login`, requestPayload)
	}

	public static signup(requestPayload: SignupRequestPayload) {
		return api.post<SignupResponsePayload>(`${this.endpoint}/signup`, requestPayload)
	}

	public static verifyPhone(requestPayload: VerifyPhoneRequestPayload) {
		return api.post<VerifyPhoneResponsePayload>(`${this.endpoint}/phone/confirm`, requestPayload)
	}

	/** Boot-state source of truth — the httpOnly session cookie is unreadable here. */
	public static me() {
		return api.get<
			ApiResponse<{
				user: StudentUser
				csrfToken: string | null
				deviceUnverified: boolean
				redirect: number | null
				chat: WaselChatIdentity | null
			}>
		>(`${this.endpoint}/me`)
	}

	/** Ends the session server-side. There was previously no such endpoint at all. */
	public static logout() {
		return api.post<ApiResponse<null>>(`${this.endpoint}/logout`)
	}

	/**
	 * Trade an admin-issued one-time code for a session cookie.
	 *
	 * Replaces the admin portal handing us a live student token in the iframe
	 * URL. The code is single-use and expires in minutes, so even though it
	 * still arrives as a query param it is worthless the moment it is spent.
	 */
	public static redeemImpersonation(code: string) {
		return api.post<ApiResponse<{ user: StudentUser; csrfToken: string }>>(`${this.endpoint}/impersonate/redeem`, { code })
	}

	/** Completes the challenge for a browser this account has not been seen on. */
	public static verifyDevice(token: string) {
		return api.post<ApiResponse<{ redirect: number | null }>>(`${this.endpoint}/device/verify`, { token })
	}

	public static resendDeviceVerification() {
		return api.post<ApiResponse<null>>(`${this.endpoint}/device/verification/resend`)
	}

	public static getEducationalInfo() {
		return api.get<EducationalInfoResponsePayload>('/education/info')
	}

	public static completeProfile(requestPayload: CompleteProfileRequestPayload) {
		return api.put<ApiResponse<null>>('/student/profile/complete', requestPayload)
	}
}
